import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Loader2, MapPin, Phone, Mail, User, Building, Truck, Clock, ChevronDown, X, CheckCircle2, Send, RefreshCw, Users, Navigation, FileText } from 'lucide-react';
import { Map, AdvancedMarker, Pin, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import LocationPickerModal from '../components/LocationPickerModal';

// --- Map Route Component ---
function MapRoute({ origin, destination, onRouteFound }) {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);

  useEffect(() => {
    if (!routesLibrary || !map) return;
    setDirectionsService(new routesLibrary.DirectionsService());
    setDirectionsRenderer(new routesLibrary.DirectionsRenderer({ map, suppressMarkers: true }));
  }, [routesLibrary, map]);

  useEffect(() => {
    if (!directionsService || !directionsRenderer || !origin || !destination) {
      if (directionsRenderer) directionsRenderer.setDirections(null);
      return;
    }
    
    console.log("Calculating route from", origin, "to", destination);
    
    directionsService.route({
      origin,
      destination,
      travelMode: 'DRIVING'
    }).then(res => {
      console.log("Route success", res);
      directionsRenderer.setDirections(res);
      const leg = res.routes[0].legs[0];
      onRouteFound({ distance: leg.distance.text, duration: leg.duration.text });
    }).catch(e => {
      console.error("Route error", e);
      onRouteFound({ error: "ไม่สามารถคำนวณเส้นทางได้ (อาจไม่มีเส้นทางรถยนต์)" });
    });
  }, [directionsService, directionsRenderer, origin, destination]);

  return null;
}
// ----------------------------

const STATUS_CONFIG = {
  'New':        { color: '#6366f1', bg: '#eef2ff', label: 'New' },
  'Contacting': { color: '#d97706', bg: '#fef3c7', label: 'Contacting' },
  'Quotation':  { color: '#2563eb', bg: '#dbeafe', label: 'Quotation' },
  'Follow-up':  { color: '#0ea5e9', bg: '#e0f2fe', label: 'Follow-up' },
  'Win':        { color: '#059669', bg: '#d1fae5', label: 'Win' },
  'Lost':       { color: '#475569', bg: '#f1f5f9', label: 'Lost' },
  'Deleted from Sheet': { color: '#ef4444', bg: '#fef2f2', label: '❌ Deleted from Sheet' },
};

const BASE = '/api';

const formatLeadDate = (lead) => {
  if (!lead) return '-';
  const val = lead.sheet_date || lead.created_at;
  if (!val) return '-';

  if (typeof val === 'string') {
    const trimmed = val.trim();
    // YYYY-MM-DD (e.g. "2026-09-22 00:00:00" or "2026-09-22")
    const matchYMD = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matchYMD) {
      const year = parseInt(matchYMD[1], 10);
      const month = parseInt(matchYMD[2], 10);
      const day = parseInt(matchYMD[3], 10);
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('th-TH');
    }
    // MM/DD/YYYY
    const matchMDY = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (matchMDY) {
      const month = parseInt(matchMDY[1], 10);
      const day = parseInt(matchMDY[2], 10);
      const year = parseInt(matchMDY[3], 10);
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('th-TH');
    }
  }

  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('th-TH');
  }
  return String(val).split(' ')[0];
};

export default function Leads() {
  const [leads, setLeads]               = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchQuery, setSearchQuery]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter]     = useState('');
  const [selectedYears, setSelectedYears] = useState([]);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isLoading, setIsLoading]       = useState(true);
  const [user, setUser]                 = useState(null);
  const [isSyncing, setIsSyncing]       = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [currentPage, setCurrentPage]   = useState(1);
  const itemsPerPage = 20;

  // Panel state
  const [selectedLead, setSelectedLead]     = useState(null);
  const [teamUsers, setTeamUsers]           = useState([]);
  const [panelAssignee, setPanelAssignee]   = useState('');
  const [panelStatus, setPanelStatus]       = useState('New');
  const [panelNote, setPanelNote]           = useState('');
  const [isSaving, setIsSaving]             = useState(false);
  const [saveMsg, setSaveMsg]               = useState(null);
  const [mapLead, setMapLead]               = useState(null);
  const [editingLocationLead, setEditingLocationLead] = useState(null);
  const [userLocation, setUserLocation]     = useState(null);
  const [routeInfo, setRouteInfo]           = useState(null);

  const handleCalculateRoute = async () => {
    if (!navigator.geolocation) {
      alert("เบราว์เซอร์ไม่รองรับระบบระบุตำแหน่ง");
      return;
    }

    // Check permission state first to give immediate feedback if denied
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      if (permission.state === 'denied') {
        alert("คุณได้ปิดกั้นการเข้าถึงตำแหน่ง (Location Permission) เอาไว้ครับ ❌\n\nวิธีแก้:\n1. สังเกตที่ช่องพิมพ์ชื่อเว็บ (URL) ด้านบน\n2. กดที่สัญลักษณ์ 🔒 (แม่กุญแจ) หรือ 📍 (พิกัด)\n3. เปลี่ยนการตั้งค่า Location (ตำแหน่ง) ให้เป็น 'Allow (อนุญาต)'\n4. รีเฟรชหน้าเว็บ 1 ครั้งแล้วลองใหม่ครับ");
        return;
      }
    } catch (e) {
      console.warn("Permission API not fully supported, proceeding to request", e);
    }

    setRouteInfo({ loading: true });
    
    let locationSet = false;
    
    const fallbackToIP = async () => {
      if (locationSet) return;
      try {
        console.log("Falling back to IP geolocation...");
        const res = await fetch('https://get.geojs.io/v1/ip/geo.json');
        const data = await res.json();
        if (data.latitude && data.longitude) {
          locationSet = true;
          console.log("IP Geolocation success:", data);
          setUserLocation({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
        } else {
          throw new Error('Invalid IP location');
        }
      } catch (err) {
        if (!locationSet) {
          setRouteInfo({ error: "ไม่สามารถดึงตำแหน่งปัจจุบันได้ (ลองใช้อินเทอร์เน็ตมือถือ หรือเปิดสิทธิ์ในเครื่องคอมพิวเตอร์)" });
        }
      }
    };

    // Request permission explicitly if possible, or just call it with timeout
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!locationSet) {
          locationSet = true;
          console.log("Geolocation success:", pos);
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        }
      },
      (err) => {
        console.error("Geolocation error:", err);
        fallbackToIP();
      },
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 }
    );
    
    // Safety net: if it hangs silently (common on Mac/Chrome), fallback after 4 seconds
    setTimeout(() => {
      fallbackToIP();
    }, 4000);
  };

  const closeMapModal = () => {
    setMapLead(null);
    setUserLocation(null);
    setRouteInfo(null);
  };

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const u = JSON.parse(userStr);
      setUser(u);
      fetchLeads(u);
      fetchTeamUsers(u);
      checkAndAutoSync();
    } else {
      setIsLoading(false);
    }
  }, []);

  const checkAndAutoSync = async () => {
    try {
      const res = await fetch(`/api/leads/sync?action=get_last_sync`);
      const data = await res.json();
      if (data.status === 'success' && data.last_sync_time) {
        setLastSyncTime(data.last_sync_time);
        const lastSync = new Date(data.last_sync_time).getTime();
        const now = new Date().getTime();
        const diffHours = (now - lastSync) / (1000 * 60 * 60);
        
        if (diffHours > 24) {
          handleSyncSheet(false);
        }
      } else {
        handleSyncSheet(false); // Never synced
      }
    } catch (err) {
      console.error('Error checking sync time', err);
    }
  };

  const handleSyncSheet = async (isManual = false) => {
    setIsSyncing(true);
    try {
      const res = await fetch(`/api/leads/sync`);
      const data = await res.json();
      if (data.status === 'success') {
        setLastSyncTime(data.last_sync_time);
        if (user) {
          fetchLeads(user); // Always refresh data after sync
        }
        if (isManual) {
          alert(data.message);
        }
      } else {
        console.error('Sync failed:', data.message);
        if (isManual) {
          alert('Sync failed: ' + data.message);
        }
      }
    } catch (err) {
      console.error('Error syncing:', err);
      if (isManual) {
        alert('Error syncing: ' + err.message);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const fetchLeads = async (u) => {
    setIsLoading(true);
    try {
      const res  = await fetch(`/api/leads?email=${u.google_email}&role=${u.role}&team_name=${u.team_name}`);
      const data = await res.json();
      if (data.status === 'success') {
        setLeads(data.data);
        setFilteredLeads(data.data);
      }
    } catch (err) {
      console.error('Error fetching leads', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeamUsers = async (u) => {
    try {
      const res  = await fetch(`/api/users`);
      const data = await res.json();
      if (data.status === 'success') {
        // Filter to only show sales / sale_manager in the same team
        const filtered = data.data.filter(
          (usr) =>
            usr.team_name === u.team_name &&
            (usr.role === 'sale' || usr.role === 'sale_manager')
        );
        setTeamUsers(filtered);
      }
    } catch (err) {
      console.error('Error fetching team users', err);
    }
  };

  const uniqueTeams = useMemo(() => {
    const teams = new Set();
    leads.forEach(l => {
      if (l.assign_to_sales_team) teams.add(l.assign_to_sales_team);
    });
    return Array.from(teams).sort();
  }, [leads]);

  const stats = useMemo(() => {
    const s = {
      total: filteredLeads.length,
      unassigned: 0,
      new: 0,
      contacting: 0,
      quotation: 0,
      win: 0,
      lost: 0
    };
    filteredLeads.forEach(l => {
      if (!l.assign_to_sales_team || l.assign_to_sales_team.trim() === '') s.unassigned++;
      if (l.lead_status === 'New') s.new++;
      if (l.lead_status === 'Contacting') s.contacting++;
      if (l.lead_status === 'Quotation') s.quotation++;
      if (l.lead_status === 'Win') s.win++;
      if (l.lead_status === 'Lost') s.lost++;
    });
    return s;
  }, [filteredLeads]);

  const teamStats = useMemo(() => {
    const ts = {};
    uniqueTeams.forEach(t => {
      ts[t] = { total: 0, new: 0, contacting: 0, quotation: 0, win: 0, lost: 0 };
    });
    ts['Unassigned'] = { total: 0, new: 0, contacting: 0, quotation: 0, win: 0, lost: 0 };
    
    filteredLeads.forEach(l => {
      const t = (!l.assign_to_sales_team || l.assign_to_sales_team.trim() === '') ? 'Unassigned' : l.assign_to_sales_team;
      if (!ts[t]) ts[t] = { total: 0, new: 0, contacting: 0, quotation: 0, win: 0, lost: 0 };
      
      ts[t].total++;
      if (l.lead_status === 'New') ts[t].new++;
      if (l.lead_status === 'Contacting') ts[t].contacting++;
      if (l.lead_status === 'Quotation') ts[t].quotation++;
      if (l.lead_status === 'Win') ts[t].win++;
      if (l.lead_status === 'Lost') ts[t].lost++;
    });
    return ts;
  }, [filteredLeads, uniqueTeams]);

  const isYearsInitialized = useRef(false);

  const uniqueYears = useMemo(() => {
    const years = new Set();
    leads.forEach(l => {
      let year;
      if (l.sheet_year && l.sheet_year.trim() !== '') {
        year = l.sheet_year.trim();
      } else if (l.sheet_date && l.sheet_date.trim() !== '') {
        const match = l.sheet_date.trim().match(/^(\d{4})/);
        year = match ? match[1] : new Date(l.sheet_date).getFullYear().toString();
      } else if (l.created_at) {
        year = new Date(l.created_at).getFullYear().toString();
      }
      
      if (year) {
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => {
      if (!isNaN(a) && !isNaN(b)) return Number(b) - Number(a);
      return b.localeCompare(a);
    });
  }, [leads]);

  useEffect(() => {
    if (uniqueYears.length > 0 && !isYearsInitialized.current) {
      setSelectedYears(uniqueYears);
      isYearsInitialized.current = true;
    }
  }, [uniqueYears]);

  useEffect(() => {
    let result = leads;

    if (uniqueYears.length > 0) {
      result = result.filter(l => {
        let year;
        if (l.sheet_year && l.sheet_year.trim() !== '') {
          year = l.sheet_year.trim();
        } else if (l.sheet_date && l.sheet_date.trim() !== '') {
          const match = l.sheet_date.trim().match(/^(\d{4})/);
          year = match ? match[1] : new Date(l.sheet_date).getFullYear().toString();
        } else if (l.created_at) {
          year = new Date(l.created_at).getFullYear().toString();
        }
        
        if (!year) return false;
        return selectedYears.includes(year);
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          (l.company_name_th && l.company_name_th.toLowerCase().includes(q)) ||
          (l.company_name_en && l.company_name_en.toLowerCase().includes(q)) ||
          (l.province && l.province.toLowerCase().includes(q)) ||
          (l.required_model && l.required_model.toLowerCase().includes(q)) ||
          (l.assign_to_sales_team && l.assign_to_sales_team.toLowerCase().includes(q)) ||
          (l.contact_name && l.contact_name.toLowerCase().includes(q)) ||
          (l.contact_phone && l.contact_phone.toLowerCase().includes(q))
      );
    }

    if (statusFilter) {
      result = result.filter(l => l.lead_status === statusFilter);
    }

    if (teamFilter) {
      if (teamFilter === 'Unassigned') {
        result = result.filter(l => !l.assign_to_sales_team || l.assign_to_sales_team.trim() === '');
      } else {
        result = result.filter(l => l.assign_to_sales_team === teamFilter);
      }
    }

    setFilteredLeads(result);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, teamFilter, selectedYears, uniqueYears, leads]);

  const openPanel = useCallback((lead) => {
    setSelectedLead(lead);
    setPanelAssignee(lead.assigned_to_user || '');
    setPanelStatus(lead.lead_status || 'New');
    setPanelNote(lead.sales_note || '');
    setSaveMsg(null);
  }, []);

  const closePanel = useCallback(() => {
    setSelectedLead(null);
    setSaveMsg(null);
  }, []);

  const handleSave = async () => {
    if (!selectedLead) return;
    setIsSaving(true);
    setSaveMsg(null);
    try {
      const res  = await fetch(`/api/leads/${selectedLead.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id:          selectedLead.sheet_running_no || selectedLead.id,
          assigned_to_user: panelAssignee,
          status:           panelStatus,
          sales_note:       panelNote,
          status_updated_by: user?.google_email,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        if (data.sheet_synced) {
          setSaveMsg({ type: 'success', text: 'บันทึกสำเร็จ (sync Google Sheet แล้ว)' });
        } else {
          setSaveMsg({ 
            type: 'error', 
            text: `บันทึกในระบบสำเร็จ แต่ไม่สามารถอัปเดต Google Sheet ได้! (${data.sheet_error || 'ไม่พบแถวใน Sheet หรือมีข้อผิดพลาด'})` 
          });
        }
        // Update local state
        const updated = leads.map((l) =>
          l.id === selectedLead.id
            ? { ...l, assigned_to_user: panelAssignee, lead_status: panelStatus, sales_note: panelNote, status_updated_by: user?.google_email, status_updated_at: data.updated_at }
            : l
        );
        setLeads(updated);
        setFilteredLeads(updated);
        setSelectedLead((prev) => ({ ...prev, assigned_to_user: panelAssignee, lead_status: panelStatus, sales_note: panelNote }));
      } else {
        setSaveMsg({ type: 'error', text: data.message || 'เกิดข้อผิดพลาด' });
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: 'Network error: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const canEditStatus = user && (user.role === 'sale' || user.role === 'sale_manager' || user.role === 'admin' || user.role === 'super_admin');
  const canEditLocation = user && (user.role === 'sale' || user.role === 'sale_manager' || user.role === 'admin' || user.role === 'super_admin' || user.role === 'marketing' || user.team_name?.toLowerCase() === 'marketing');

  const tableContent = useMemo(() => {
    if (isLoading) {
      return (
        <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#64748b', gap: '0.5rem' }}>
          <Loader2 className="lucide-spin" size={24} color="var(--color-primary)" /> Loading leads...
        </div>
      );
    }
    
    if (filteredLeads.length === 0) {
      return (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          No leads found.
        </div>
      );
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedLeads = filteredLeads.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1200px', borderCollapse: 'collapse', textAlign: 'left', tableLayout: 'fixed' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f1f5f9' }}>
              <tr style={{ color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--color-border)' }}>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '18%' }}>Customer</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '18%' }}>Contact Info</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '16%' }}>Requirement</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '16%' }}>Location</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '10%' }}>Assigned Team</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '10%' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, width: '100px' }}>Date</th>
                {canEditStatus && (
                  <th style={{ padding: '0.75rem 0.5rem', fontWeight: 600, width: '120px' }}>Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.map((lead) => {
            const cfg = STATUS_CONFIG[lead.lead_status] || STATUS_CONFIG['New'];
            return (
              <tr
                key={lead.id}
                style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s', height: '40px' }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <Building size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }} title={lead.company_name_th || 'N/A'}>
                      {lead.company_name_th || 'N/A'}
                    </span>
                  </div>
                  {lead.assigned_to_user && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <User size={10} style={{ flexShrink: 0 }} /> 
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={lead.assigned_to_user}>
                        {lead.assigned_to_user}
                      </span>
                    </div>
                  )}
                </td>

                <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.8rem', color: '#475569' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }} title={lead.contact_name || '-'}>
                      <User size={12} style={{ flexShrink: 0 }} /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.contact_name || '-'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }} title={lead.contact_phone || '-'}>
                      <Phone size={12} style={{ flexShrink: 0 }} /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.contact_phone || '-'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }} title={lead.contact_email || '-'}>
                      <Mail size={12} style={{ flexShrink: 0 }} /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.contact_email || '-'}</span>
                    </div>
                  </div>
                </td>

                <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.8rem' }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.35rem' }} title={lead.required_model || '-'}>
                      <Truck size={12} style={{ color: '#d97706', flexShrink: 0 }} /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.required_model || '-'}</span>
                    </div>
                    <div>
                      <span style={{ display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600, backgroundColor: lead.condition_type === 'New Truck' ? '#dcfce7' : '#fef3c7', color: lead.condition_type === 'New Truck' ? '#166534' : '#92400e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }} title={lead.condition_type || '-'}>
                        {lead.condition_type || '-'}
                      </span>
                    </div>
                    {lead.required_option && <div style={{ color: '#64748b', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`Options: ${lead.required_option}`}>Options: {lead.required_option}</div>}
                  </div>
                </td>

                <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontSize: '0.8rem', color: '#475569' }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={lead.province || '-'}>{lead.province || '-'}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={lead.region || '-'}>{lead.region || '-'}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem' }} title={lead.address || '-'}>
                      <MapPin size={10} style={{ flexShrink: 0 }} />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.address || 'ยังไม่ระบุที่อยู่'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                      {lead.lat && lead.lng ? (
                        <>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setMapLead(lead); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.45rem', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.7rem', color: '#475569', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                          >
                            <MapPin size={10} /> ดูแผนที่
                          </button>
                          {canEditLocation && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); setEditingLocationLead(lead); }}
                              title="แก้ไขพิกัดบนแผนที่"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.2rem 0.45rem', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '0.7rem', color: '#2563eb', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                            >
                              ✏️ แก้ไขพิกัด
                            </button>
                          )}
                        </>
                      ) : (
                        canEditLocation && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setEditingLocationLead(lead); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.55rem', backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '0.7rem', color: '#1d4ed8', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                          >
                            <MapPin size={10} /> 📍 ปักหมุดแผนที่
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </td>

                <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#e0f2fe', color: '#0369a1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }} title={lead.assign_to_sales_team || 'Unassigned'}>
                    {lead.assign_to_sales_team || 'Unassigned'}
                  </span>
                </td>

                <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700, backgroundColor: cfg.bg, color: cfg.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }} title={cfg.label}>
                    {cfg.label}
                  </span>
                  {lead.status_updated_at && (
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`by ${lead.status_updated_by?.split('@')[0]}`}>
                      by {lead.status_updated_by?.split('@')[0]}
                    </div>
                  )}
                </td>

                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#64748b', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', whiteSpace: 'nowrap' }} title={lead.sheet_date ? `Google Sheet Date: ${lead.sheet_date}` : `Created: ${lead.created_at}`}>
                    <Clock size={12} />
                    {formatLeadDate(lead)}
                  </div>
                </td>

                {canEditStatus && (
                  <td style={{ padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
                    <button
                      onClick={() => openPanel(lead)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap', transition: 'all 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                      onMouseOver={(e) => { e.currentTarget.style.background = '#eb0a1e'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#eb0a1e'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                    >
                      <FileText size={13} /> Detail
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', backgroundColor: '#fff', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', flexShrink: 0 }}>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredLeads.length)} of {filteredLeads.length} leads
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: '0.35rem 0.75rem', border: '1px solid #e2e8f0', backgroundColor: currentPage === 1 ? '#f8fafc' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#1e293b', borderRadius: '6px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}
            >
              Prev
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1).map((p, idx, arr) => {
              const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
              return (
                <React.Fragment key={p}>
                  {showEllipsis && <span style={{ padding: '0.35rem 0.25rem', color: '#94a3b8' }}>...</span>}
                  <button
                    onClick={() => setCurrentPage(p)}
                    style={{ padding: '0.35rem 0.75rem', border: '1px solid #e2e8f0', backgroundColor: currentPage === p ? '#eb0a1e' : '#fff', color: currentPage === p ? '#fff' : '#1e293b', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: currentPage === p ? 600 : 400 }}
                  >
                    {p}
                  </button>
                </React.Fragment>
              );
            })}
            
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{ padding: '0.35rem 0.75rem', border: '1px solid #e2e8f0', backgroundColor: currentPage === totalPages ? '#f8fafc' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#1e293b', borderRadius: '6px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
    );
  }, [isLoading, filteredLeads, canEditStatus, canEditLocation, openPanel, currentPage, itemsPerPage]);

  return (
    <div style={{ padding: '2rem', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ===== Header ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#1f2937', margin: 0 }}>โอกาสทางการขาย</h1>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '0.25rem' }}>Manage and follow up on marketing leads</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {lastSyncTime && (
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              Sync ล่าสุด: {new Date(lastSyncTime).toLocaleString('th-TH')}
            </span>
          )}
          <button
            onClick={() => handleSyncSheet(true)}
            disabled={isSyncing}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: isSyncing ? '#f1f5f9' : '#fff', color: '#475569', cursor: isSyncing ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 500 }}
          >
            {isSyncing ? <Loader2 size={15} className="lucide-spin" /> : <RefreshCw size={15} />}
            {isSyncing ? 'Syncing...' : 'Sync Data'}
          </button>
          <button
            onClick={() => user && fetchLeads(user)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      {/* ===== Mini Dashboard ===== */}
      {!isLoading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #64748b' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>ทั้งหมด</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>{stats.total}</div>
          </div>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #ef4444', backgroundColor: '#fef2f2' }}>
            <div style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>รอจ่ายงาน</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7f1d1d' }}>{stats.unassigned}</div>
          </div>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #6366f1' }}>
            <div style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 600, textTransform: 'uppercase' }}>New</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#3730a3' }}>{stats.new}</div>
          </div>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 600, textTransform: 'uppercase' }}>Contacting</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#92400e' }}>{stats.contacting}</div>
          </div>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600, textTransform: 'uppercase' }}>Quotation</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e40af' }}>{stats.quotation}</div>
          </div>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600, textTransform: 'uppercase' }}>Win</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#065f46' }}>{stats.win}</div>
          </div>
          <div className="card" style={{ padding: '1rem', borderLeft: '4px solid #94a3b8' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Lost</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#475569' }}>{stats.lost}</div>
          </div>
        </div>
      )}

      {/* ===== Team Breakdown ===== */}
      {!isLoading && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Users size={15} style={{ color: 'var(--color-primary)' }} />
            จำนวน Lead แบ่งตามทีมขายและสถานะ (Team Performance)
          </h3>
          <div style={{ overflowX: 'auto', maxHeight: '250px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b', textTransform: 'uppercase' }}>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600 }}>Team</th>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600 }}>Total</th>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600, color: '#4f46e5' }}>New</th>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600, color: '#d97706' }}>Contacting</th>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600, color: '#2563eb' }}>Quotation</th>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600, color: '#059669' }}>Win</th>
                  <th style={{ padding: '0.4rem 0.75rem', fontWeight: 600, color: '#475569' }}>Lost</th>
                </tr>
              </thead>
              <tbody>
                {['Unassigned', ...uniqueTeams].filter(t => teamStats[t] && teamStats[t].total > 0).map(t => (
                  <tr key={t} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.35rem 0.75rem', fontWeight: 600, color: t === 'Unassigned' ? '#ef4444' : '#0f172a' }}>
                      {t === 'Unassigned' ? 'Unassigned' : t}
                    </td>
                    <td style={{ padding: '0.35rem 0.75rem', fontWeight: 700 }}>{teamStats[t].total}</td>
                    <td style={{ padding: '0.35rem 0.75rem' }}>{teamStats[t].new > 0 ? <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 }}>{teamStats[t].new}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                    <td style={{ padding: '0.35rem 0.75rem' }}>{teamStats[t].contacting > 0 ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 }}>{teamStats[t].contacting}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                    <td style={{ padding: '0.35rem 0.75rem' }}>{teamStats[t].quotation > 0 ? <span style={{ background: '#dbeafe', color: '#1e40af', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 }}>{teamStats[t].quotation}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                    <td style={{ padding: '0.35rem 0.75rem' }}>{teamStats[t].win > 0 ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 }}>{teamStats[t].win}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                    <td style={{ padding: '0.35rem 0.75rem' }}>{teamStats[t].lost > 0 ? <span style={{ background: '#f1f5f9', color: '#475569', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 }}>{teamStats[t].lost}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Main Card ===== */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: '600px' }}>
        {/* Toolbar */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: '280px', flexShrink: 0 }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
              <input
                type="text"
                placeholder="Search leads..."
                className="input-field"
                style={{ paddingLeft: '2.5rem', backgroundColor: '#ffffff', width: '100%' }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
              style={{ backgroundColor: '#ffffff', width: 'auto', minWidth: '180px', cursor: 'pointer', flexShrink: 0 }}
            >
              <option value="">ทุกสถานะ (All Status)</option>
              {Object.keys(STATUS_CONFIG).map(k => (
                <option key={k} value={k}>{STATUS_CONFIG[k].label.replace(/[\u{1F3C6}\u{274C}]/gu, '').trim()}</option>
              ))}
            </select>

            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="input-field"
              style={{ backgroundColor: '#ffffff', width: 'auto', minWidth: '180px', cursor: 'pointer', flexShrink: 0 }}
            >
              <option value="">ทุกทีม (All Teams)</option>
              <option value="Unassigned">Unassigned (ยังไม่มอบหมาย)</option>
              {uniqueTeams.map(t => (
                <option key={t} value={t}>ทีม {t}</option>
              ))}
            </select>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                className="input-field"
                style={{ backgroundColor: '#ffffff', width: 'auto', minWidth: '150px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
              >
                <span style={{ fontSize: '0.85rem' }}>
                  ปี: {selectedYears.length === uniqueYears.length ? 'ทั้งหมด (All)' : selectedYears.length === 0 ? 'ไม่เลือกเลย' : selectedYears.join(', ')}
                </span>
                <ChevronDown size={14} style={{ marginLeft: '0.5rem' }} />
              </button>

              {isYearDropdownOpen && (
                <>
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }} onClick={() => setIsYearDropdownOpen(false)} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '180px', padding: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                      <button onClick={() => setSelectedYears(uniqueYears)} style={{ fontSize: '0.75rem', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Select All</button>
                      <button onClick={() => setSelectedYears([])} style={{ fontSize: '0.75rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Unselect All</button>
                    </div>
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {uniqueYears.map(y => (
                        <label key={y} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', cursor: 'pointer', fontSize: '0.85rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedYears.includes(y)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedYears([...selectedYears, y].sort((a, b) => {
                                  if (!isNaN(a) && !isNaN(b)) return Number(b) - Number(a);
                                  return b.localeCompare(a);
                                }));
                              } else {
                                setSelectedYears(selectedYears.filter(year => year !== y));
                              }
                            }}
                          />
                          {y}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500, flexShrink: 0 }}>
            Showing <strong>{filteredLeads.length}</strong> leads
          </div>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflowX: 'auto', display: 'flex', flexDirection: 'column', minHeight: '500px' }}>
          {tableContent}
        </div>
      </div>

      {/* ===== Lead Detail & Update Modal (Center Popup) ===== */}
      {selectedLead && (
        <>
          {/* Overlay */}
          <div
            onClick={closePanel}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1000, backdropFilter: 'blur(4px)' }}
          />

          {/* Modal Container */}
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1001,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem', pointerEvents: 'none'
          }}>
            <div style={{
              width: '100%', maxWidth: '780px', maxHeight: '90vh',
              background: '#fff', borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              pointerEvents: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#f8fafc' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#eb0a1e', backgroundColor: '#fee2e2', padding: '0.15rem 0.55rem', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Lead Detail
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>
                      Lead ID: <strong style={{ color: '#0f172a' }}>{selectedLead.sheet_running_no || selectedLead.id}</strong>
                    </span>
                    {selectedLead.sheet_no && (
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                        (No. {selectedLead.sheet_no})
                      </span>
                    )}
                    {selectedLead.lead_status && (
                      <span style={{ 
                        fontSize: '0.72rem', fontWeight: 700, 
                        backgroundColor: (STATUS_CONFIG[selectedLead.lead_status] || STATUS_CONFIG['New']).bg, 
                        color: (STATUS_CONFIG[selectedLead.lead_status] || STATUS_CONFIG['New']).color,
                        padding: '0.15rem 0.55rem', borderRadius: '999px'
                      }}>
                        {(STATUS_CONFIG[selectedLead.lead_status] || STATUS_CONFIG['New']).label}
                      </span>
                    )}
                  </div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Building size={20} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                    {selectedLead.company_name_th || selectedLead.customer_name || 'N/A'}
                  </h2>
                  {selectedLead.company_name_en && (
                    <div style={{ fontSize: '0.83rem', color: '#64748b', marginTop: '0.15rem', paddingLeft: '1.75rem' }}>
                      {selectedLead.company_name_en}
                    </div>
                  )}
                </div>
                <button 
                  onClick={closePanel} 
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#64748b', padding: '0.45rem', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                  onMouseOver={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#eb0a1e'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body (Scrollable) */}
              <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                {/* Section 1: Requirement & Requirement Detail (จุดสำคัญที่ขอเพิ่มเติม) */}
                <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Truck size={16} style={{ color: '#d97706' }} /> ข้อมูลความต้องการของลูกค้า (Requirements)
                  </div>
                  
                  {/* Highlight Box for Requirement Detail */}
                  <div style={{ marginBottom: '1rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '0.9rem 1.1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#92400e', marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <FileText size={15} /> รายละเอียดความต้องการ (Requirement Detail):
                    </div>
                    <div style={{ fontSize: '0.92rem', color: '#78350f', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 500 }}>
                      {selectedLead.requirement_detail && selectedLead.requirement_detail.trim() !== '' 
                        ? selectedLead.requirement_detail 
                        : <span style={{ color: '#b45309', fontStyle: 'italic' }}>— ไม่มีข้อมูลรายละเอียดเพิ่มเติม —</span>}
                    </div>
                  </div>

                  {/* Spec Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                    <div style={{ background: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>ประเภทรถ (Type)</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', marginTop: '0.15rem' }}>{selectedLead.requirement_type || '-'}</div>
                    </div>
                    <div style={{ background: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>รุ่นที่ต้องการ (Model)</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', marginTop: '0.15rem' }}>{selectedLead.required_model || '-'}</div>
                    </div>
                    <div style={{ background: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>สภาพรถ (New/Used)</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', marginTop: '0.15rem' }}>{selectedLead.condition_type || '-'}</div>
                    </div>
                    <div style={{ background: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>ตัวเลือก (Option)</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', marginTop: '0.15rem' }}>{selectedLead.required_option || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Contact & Location */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                  {/* Contact Box */}
                  <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <User size={15} style={{ color: '#0284c7' }} /> ข้อมูลผู้ติดต่อ (Contact Info)
                    </div>
                    <div style={{ fontSize: '0.83rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <User size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>ชื่อผู้ติดต่อ: <strong>{selectedLead.contact_name || '-'}</strong></span>
                    </div>
                    <div style={{ fontSize: '0.83rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Phone size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>เบอร์โทร: <strong>{selectedLead.contact_phone || '-'}</strong></span>
                    </div>
                    <div style={{ fontSize: '0.83rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Mail size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>อีเมล: <strong>{selectedLead.contact_email || '-'}</strong></span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.5rem', borderTop: '1px dashed #f1f5f9', paddingTop: '0.45rem', marginTop: '0.15rem' }}>
                      <span>ช่องทาง: <strong>{selectedLead.channel || '-'}</strong></span>
                      <span style={{ color: '#cbd5e1' }}>•</span>
                      <span>ประเภท: <strong>{selectedLead.customer_type || '-'}</strong></span>
                    </div>
                  </div>

                  {/* Location & Team Box */}
                  <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <MapPin size={15} style={{ color: '#16a34a' }} /> สถานที่และทีมที่รับผิดชอบ
                      </div>
                      {canEditLocation && (
                        <button
                          type="button"
                          onClick={() => setEditingLocationLead(selectedLead)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.25rem 0.6rem',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #93c5fd',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            color: '#1d4ed8',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                        >
                          <MapPin size={12} /> {selectedLead.lat && selectedLead.lng ? 'แก้ไขพิกัดแผนที่' : '📍 ปักหมุดบนแผนที่'}
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: '0.83rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <MapPin size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>พื้นที่: <strong>{selectedLead.province || '-'}</strong> ({selectedLead.region || '-'})</span>
                      {selectedLead.lat && selectedLead.lng && (
                        <button
                          type="button"
                          onClick={() => setMapLead(selectedLead)}
                          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.5rem', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.7rem', color: '#475569', cursor: 'pointer', fontWeight: 600 }}
                          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
                          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        >
                          <MapPin size={10} /> ดูบนแผนที่
                        </button>
                      )}
                    </div>
                    {selectedLead.address && (
                      <div style={{ fontSize: '0.8rem', color: '#64748b', paddingLeft: '1.25rem' }}>
                        {selectedLead.address}
                      </div>
                    )}
                    <div style={{ fontSize: '0.83rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Users size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>ทีมฝ่ายขาย: <strong style={{ color: '#0369a1' }}>{selectedLead.assign_to_sales_team || 'Unassigned'}</strong></span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.4rem', borderTop: '1px dashed #f1f5f9', paddingTop: '0.45rem', marginTop: '0.15rem' }}>
                      <Clock size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>วันที่บันทึก (Sheet): <strong>{formatLeadDate(selectedLead)}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Section 3: Sales Assignment & Status Management */}
                <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    📊 การจัดการและอัปเดตสถานะ (Sales Management)
                  </div>

                  {/* Assign to salesperson */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                      <User size={13} style={{ display: 'inline', marginRight: '0.35rem', verticalAlign: 'middle' }} />
                      ผู้รับ Lead (Salesperson)
                    </label>
                    <select
                      value={panelAssignee}
                      onChange={(e) => setPanelAssignee(e.target.value)}
                      className="input-field"
                      style={{ width: '100%', fontSize: '0.85rem', padding: '0.55rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}
                    >
                      <option value="">— ยังไม่ได้ assign —</option>
                      {teamUsers.map((u) => (
                        <option key={u.id} value={u.google_email}>
                          {u.google_email} ({u.role === 'sale_manager' ? 'Manager' : 'Sale'})
                        </option>
                      ))}
                    </select>
                    {teamUsers.length === 0 && (
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>ไม่พบ user ในทีมนี้</p>
                    )}
                  </div>

                  {/* Status Buttons */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.45rem' }}>
                      สถานะ Lead (Status)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '0.5rem' }}>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => setPanelStatus(key)}
                          style={{
                            padding: '0.55rem 0.65rem',
                            borderRadius: '8px',
                            border: `2px solid ${panelStatus === key ? cfg.color : '#e2e8f0'}`,
                            background: panelStatus === key ? cfg.bg : '#fff',
                            color: panelStatus === key ? cfg.color : '#64748b',
                            fontWeight: panelStatus === key ? 700 : 500,
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem'
                          }}
                        >
                          {panelStatus === key && <CheckCircle2 size={13} />}
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Note */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                      📝 Note บันทึกการติดตาม (สำหรับฝ่ายขาย)
                    </label>
                    <textarea
                      value={panelNote}
                      onChange={(e) => setPanelNote(e.target.value)}
                      placeholder="เพิ่มโน้ตการติดตาม เช่น ติดต่อลูกค้าแล้ว, ส่งใบเสนอราคาแล้ว, นัดหมายเข้าดูหน้างาน..."
                      style={{ width: '100%', minHeight: '85px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Updated by info */}
                  {selectedLead.status_updated_by && (
                    <div style={{ padding: '0.65rem 0.9rem', background: '#f1f5f9', borderRadius: '8px', fontSize: '0.78rem', color: '#64748b' }}>
                      <strong>Last updated by:</strong> {selectedLead.status_updated_by}
                      {selectedLead.status_updated_at && (
                        <span> ({new Date(selectedLead.status_updated_at).toLocaleString('th-TH')})</span>
                      )}
                    </div>
                  )}

                  {/* Save message */}
                  {saveMsg && (
                    <div style={{
                      padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 500,
                      background: saveMsg.type === 'success' ? '#ecfdf5' : '#fef2f2',
                      color: saveMsg.type === 'success' ? '#065f46' : '#991b1b',
                      border: `1px solid ${saveMsg.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                    }}>
                      {saveMsg.type === 'success' ? '✅ ' : '❌ '}{saveMsg.text}
                    </div>
                  )}
                </div>

              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: '#f8fafc' }}>
                <button
                  onClick={closePanel}
                  style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}
                >
                  ปิดหน้าต่าง
                </button>
                {canEditStatus && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{ padding: '0.6rem 1.5rem', borderRadius: '8px', border: 'none', background: isSaving ? '#94a3b8' : '#eb0a1e', color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'background 0.2s', boxShadow: '0 2px 4px rgba(235, 10, 30, 0.2)' }}
                  >
                    {isSaving ? <><Loader2 size={16} className="lucide-spin" /> กำลังบันทึก...</> : <><Send size={16} /> บันทึกการอัปเดต</>}
                  </button>
                )}
              </div>

            </div>
          </div>
        </>
      )}

      {/* ===== Map Modal ===== */}
      {mapLead && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '600px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1e293b' }}>
                <MapPin size={18} color="var(--color-primary)" />
                แผนที่ตำแหน่งลูกค้า
              </h3>
              <button onClick={closeMapModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
            </div>
            
            {/* Map Area */}
            <div style={{ height: '350px', position: 'relative', flexShrink: 0 }}>
              <Map
                mapId="LEAD_MAP_MODAL"
                defaultZoom={15}
                defaultCenter={{ lat: parseFloat(mapLead.lat), lng: parseFloat(mapLead.lng) }}
                gestureHandling="greedy"
                disableDefaultUI={true}
              >
                <AdvancedMarker position={{ lat: parseFloat(mapLead.lat), lng: parseFloat(mapLead.lng) }}>
                  <Pin background="var(--color-primary)" borderColor="#b91c1c" glyphColor="#fff" />
                </AdvancedMarker>
                {userLocation && (
                  <>
                    <AdvancedMarker position={userLocation}>
                      <Pin background="#3b82f6" borderColor="#1d4ed8" glyphColor="#fff" />
                    </AdvancedMarker>
                    <MapRoute 
                      origin={userLocation} 
                      destination={{ lat: parseFloat(mapLead.lat), lng: parseFloat(mapLead.lng) }} 
                      onRouteFound={(info) => {
                        setRouteInfo(prev => {
                          if (prev?.distance === info.distance && prev?.duration === info.duration && prev?.error === info.error) return prev;
                          return info;
                        });
                      }} 
                    />
                  </>
                )}
              </Map>
            </div>

            {/* Info Area */}
            <div style={{ padding: '1.25rem 1.5rem', backgroundColor: '#f8fafc', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem', marginBottom: '0.35rem' }}>{mapLead.company_name_th || mapLead.customer_name || 'ไม่ระบุชื่อบริษัท'}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.85rem', color: '#475569', marginBottom: '0.25rem' }}>
                    <User size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{mapLead.contact_name || 'ไม่ระบุชื่อผู้ติดต่อ'} {mapLead.contact_phone && `(${mapLead.contact_phone})`}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                    <MapPin size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{mapLead.address} {mapLead.province} {mapLead.region}</span>
                  </div>
                </div>

                {/* Route Button / Info */}
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  {!userLocation && !routeInfo?.loading ? (
                    <button 
                      onClick={handleCalculateRoute}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.85rem', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      <Navigation size={14} /> คำนวณเส้นทาง
                    </button>
                  ) : routeInfo?.loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#3b82f6', fontSize: '0.85rem', fontWeight: 600 }}>
                      <Loader2 size={16} className="lucide-spin" /> กำลังคำนวณ...
                    </div>
                  ) : routeInfo?.error ? (
                    <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 500 }}>{routeInfo.error}</div>
                  ) : routeInfo?.distance && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ backgroundColor: '#eff6ff', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #bfdbfe', textAlign: 'left' }}>
                        <div style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600, marginBottom: '0.15rem' }}>ระยะทาง: <span style={{ color: '#1e3a8a', fontSize: '0.85rem' }}>{routeInfo.distance}</span></div>
                        <div style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600 }}>เวลาเดินทาง: <span style={{ color: '#1e3a8a', fontSize: '0.85rem' }}>{routeInfo.duration}</span></div>
                      </div>
                      <button
                        onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${mapLead.lat},${mapLead.lng}&travelmode=driving`, '_blank')}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', padding: '0.4rem', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'background-color 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      >
                        <Navigation size={14} /> นำทาง (Google Maps)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={!!editingLocationLead}
        lead={editingLocationLead}
        onClose={() => setEditingLocationLead(null)}
        onLocationSaved={(updatedLead) => {
          setLeads(prev => prev.map(l => l.id === updatedLead.id ? { ...l, ...updatedLead } : l));
          setFilteredLeads(prev => prev.map(l => l.id === updatedLead.id ? { ...l, ...updatedLead } : l));
          if (selectedLead && selectedLead.id === updatedLead.id) {
            setSelectedLead(prev => ({ ...prev, ...updatedLead }));
          }
          setEditingLocationLead(null);
        }}
      />
    </div>
  );
}
